<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="440">
        <v-card rounded="xl" class="filter-dialog">
            <!-- Header -->
            <div class="filter-header pa-5 pb-3">
                <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center ga-3">
                        <div class="filter-icon-circle">
                            <v-icon icon="mdi-tune-variant" size="20" color="white"/>
                        </div>
                        <div>
                            <h3 class="text-h6 font-weight-bold">Filter Trackers</h3>
                            <p class="text-caption text-medium-emphasis mb-0">Narrow down your trackers</p>
                        </div>
                    </div>
                    <v-btn density="compact" icon="mdi-close" variant="text" size="small"
                           @click="$emit('update:modelValue', false)"/>
                </div>

                <!-- Active filter count -->
                <div v-if="activeFilterCount > 0" class="mt-3">
                    <v-chip size="small" color="primary" variant="tonal" closable
                            @click:close="resetFilters">
                        {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active
                    </v-chip>
                </div>
            </div>

            <v-divider/>

            <!-- Filter Cards -->
            <v-card-text class="pa-4">
                <v-alert v-if="errors.length" class="mb-4" type="error" variant="tonal" closable density="compact">
                    <p v-for="error in errors" :key="error" class="mb-0">{{ error }}</p>
                </v-alert>

                <div class="d-flex flex-column ga-3">
                    <template v-for="key in visibleFilterKeys" :key="key">
                        <v-card variant="outlined" rounded="lg"
                                :class="{'filter-card-active': formData[key]?.data != null && formData[key]?.data !== ''}"
                                class="filter-card">
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center ga-2 mb-2">
                                    <v-icon :icon="filters[key].icon || 'mdi-filter'" size="18"
                                            :color="formData[key]?.data != null && formData[key]?.data !== '' ? 'primary' : 'grey'"/>
                                    <span class="text-body-2 font-weight-medium">{{ filters[key].label }}</span>
                                    <v-spacer/>
                                    <v-btn v-if="formData[key]?.data != null && formData[key]?.data !== ''"
                                           icon="mdi-close-circle" size="x-small" variant="text" color="grey"
                                           @click="clearFilter(key)"/>
                                </div>

                                <!-- Select filters (Activity, Status) -->
                                <template v-if="filters[key].type === 'select'">
                                    <v-select
                                        v-model="formData[key]['data']"
                                        :items="filters[key].source"
                                        :item-title="filters[key].title"
                                        :item-value="filters[key].value"
                                        :placeholder="filters[key].description"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        rounded="lg"
                                    />
                                </template>

                                <!-- Geo radius filter (Nearby) -->
                                <template v-else-if="key === 'geo_radius'">
                                    <div class="d-flex align-center ga-2">
                                        <v-text-field
                                            v-model="formData[key]['data']"
                                            placeholder="Radius (km)"
                                            type="number"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            rounded="lg"
                                            style="max-width: 120px"
                                        />
                                        <v-text-field
                                            v-model="formData[key]['latitude']"
                                            placeholder="Latitude"
                                            type="number"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            rounded="lg"
                                        />
                                        <v-text-field
                                            v-model="formData[key]['longitude']"
                                            placeholder="Longitude"
                                            type="number"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            rounded="lg"
                                        />
                                        <v-btn icon="mdi-map-marker" variant="tonal" color="primary" size="small"
                                               rounded="lg" @click="showMapDialog"/>
                                    </div>
                                </template>

                                <!-- Action-able filters (Serial Number, IMEI, ICC ID, Last Seen,
                                     Battery Voltage): condition + value. The API rejects one of
                                     these sent with a value and no condition, so a typed value
                                     gets the key's default condition (filterActions.js) until
                                     the user picks another. -->
                                <template v-else-if="filters[key].hasOwnProperty('action')">
                                    <div class="d-flex ga-2">
                                        <v-select
                                            v-model="formData[key]['action']"
                                            :items="operatorsFor(key)"
                                            item-title="description"
                                            item-value="abbr"
                                            placeholder="Condition"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            rounded="lg"
                                            style="max-width: 150px"
                                        />
                                        <v-text-field
                                            v-model="formData[key]['data']"
                                            :placeholder="filters[key].description"
                                            :type="filters[key].type ?? 'text'"
                                            :step="filters[key].type === 'number' ? '0.1' : undefined"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            rounded="lg"
                                        />
                                    </div>
                                </template>

                                <!-- Plain text filters (Tracker Name) -->
                                <template v-else>
                                    <v-text-field
                                        v-model="formData[key]['data']"
                                        :placeholder="filters[key].description"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        rounded="lg"
                                    />
                                </template>
                            </v-card-text>
                        </v-card>
                    </template>
                </div>
            </v-card-text>

            <v-divider/>

            <!-- Actions -->
            <v-card-actions class="pa-4 d-flex ga-2">
                <!-- Saved Filters -->
                <v-select
                    v-if="filterProfiles && filterProfiles.length"
                    v-model="selectedFilterProfile"
                    :items="filterProfiles"
                    clearable
                    density="compact"
                    hide-details
                    hide-no-data
                    item-title="label"
                    item-value="id"
                    placeholder="Saved filters"
                    max-width="140"
                    return-object
                    variant="outlined"
                    rounded="lg"
                    @update:modelValue="applyFilterProfile"
                />
                <v-spacer/>
                <v-btn variant="text" size="small" @click="saveFilterProfile" :disabled="checkEmptyForm">
                    <v-icon start icon="mdi-content-save-outline" size="16"/>
                    Save
                </v-btn>
                <v-btn variant="outlined" rounded="lg" size="small" @click="resetFilters" :disabled="checkEmptyForm">
                    Reset
                </v-btn>
                <v-btn color="primary" rounded="lg" variant="flat" size="small"
                       @click="applyFilters" :disabled="checkEmptyForm">
                    <v-icon start icon="mdi-check" size="16"/>
                    Apply
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Map-Dialog v-if="mapDialogSwitch" v-model="mapDialogSwitch" :setMapValues="mapValues" @mapValue="mapValue"/>

    <v-dialog v-model="filterProfileDialogSwitch" persistent width="400">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="storeFilterProfile">
            <v-card rounded="xl">
                <v-card-title class="d-flex justify-space-between pa-5">
                    <h4 class="text-h6 font-weight-bold">Save Filter</h4>
                    <v-btn density="compact" icon="mdi-close" variant="text" size="small"
                           @click="filterProfileDialogSwitch = false"/>
                </v-card-title>

                <v-card-text class="px-5 pt-0">
                    <v-alert v-if="filterProfileErrors.length" class="mb-3" type="error" variant="tonal"
                             density="compact" closable>
                        <p v-for="error in filterProfileErrors" :key="error" class="mb-0">{{ error }}</p>
                    </v-alert>
                    <v-text-field
                        v-model="filterProfileFormData['label']"
                        :rules="[vRules.required]"
                        label="Filter name"
                        placeholder="e.g. Kids at school, Pets nearby"
                        variant="outlined"
                        density="compact"
                        rounded="lg"
                    />
                </v-card-text>

                <v-card-actions class="px-5 pb-5 d-flex justify-end ga-2">
                    <v-btn variant="outlined" rounded="lg" size="small"
                           @click="filterProfileDialogSwitch = false">Cancel</v-btn>
                    <v-btn :disabled="!filterProfileFormData['label']" color="primary" variant="flat"
                           rounded="lg" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import MapDialog from "@/components/cards/devices/MapDialog.vue";
import {operatorsFor, fillDefaultActions} from "@/composables/filterActions";

const errors = ref([]);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    filters: Object,
});
const formData = reactive(props.filters);
const filterProfileFormData = reactive({
    label: null,
    configs: {}
});
const emits = defineEmits([
    "update:modelValue",
    "resetFilters",
    "applyFilters"
]);
const checkEmptyForm = ref(true);
const mapDialogSwitch = ref(false);
const filterProfiles = ref(null);
const filterProfileErrors = ref([]);
const selectedFilterProfile = ref(null);
const dialogSwitch = ref(props.modelValue);
const filterProfileDialogSwitch = ref(false);
const mapValues = ref({
    type: 0,
    center_lat: null,
    center_lon: null,
    radius: null,
});


const vRules = {
    required: (value) => !!value || 'Required',
};

// Only show non-hidden filters
const visibleFilterKeys = computed(() => {
    return Object.keys(formData).filter(key => formData[key]?.type !== 'hidden');
});

const activeFilterCount = computed(() => {
    let count = 0;
    for (const key of visibleFilterKeys.value) {
        if (formData[key]?.data != null && formData[key]?.data !== '') count++;
    }
    return count;
});

const clearFilter = (key) => {
    formData[key].data = null;
    if (formData[key].hasOwnProperty('action')) formData[key].action = null;
    if (formData[key].hasOwnProperty('latitude')) formData[key].latitude = null;
    if (formData[key].hasOwnProperty('longitude')) formData[key].longitude = null;
};

const showMapDialog = () => {
    mapDialogSwitch.value = true;
    mapValues.value = {
        type: 0,
        center_lat: formData["geo_radius"]["latitude"],
        center_lon: formData["geo_radius"]["longitude"],
        radius: formData["geo_radius"]["data"],
    };
};

const fetchFilterProfile = () => {
    send('get', 'filter-profiles?list=1')
        .then((response) => {
            filterProfiles.value = response.data.data;
        }).catch(() => {
        snackbar.showSnackbar('Filter profiles could not be fetched', 'error')
    })
}
const saveFilterProfile = () => {
    if (checkEmptyForm.value) return;

    if (selectedFilterProfile.value) {
        filterProfileFormData['label'] = selectedFilterProfile.value.label;
    }
    Object.entries(formData).forEach(([key, value]) => {
        if (value['data'] === null) return;

        filterProfileFormData['configs'][key] = filterProfileFormData['configs'][key] || {};

        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (value.hasOwnProperty(field) && value[field] !== null) {
                filterProfileFormData['configs'][key][field] = value[field];
            }
        });
    });

    filterProfileDialogSwitch.value = true;
}
const storeFilterProfile = () => {
    filterProfileErrors.value = [];
    if (filterProfileFormData['configs']) {
        send(selectedFilterProfile.value ? 'put' : 'post', `filter-profiles${selectedFilterProfile.value ? '/' + selectedFilterProfile.value.id : ''}`, filterProfileFormData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    if (selectedFilterProfile.value) {
                        filterProfiles.value.map((filterProfile, index) => {
                            if (filterProfile.id === selectedFilterProfile.value.id) {
                                filterProfiles.value[index] = response.data.data;
                            }
                        })
                    } else {
                        filterProfiles.value.push(response.data.data);
                    }
                    filterProfileDialogSwitch.value = false;
                    filterProfileFormData['label'] = null;
                    filterProfileFormData['configs'] = {};
                }
            }).catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    filterProfileErrors.value.push(validationErrors[key][0]);
                });
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
    }
}
const applyFilterProfile = () => {
    emits('resetFilters', false);
    if (selectedFilterProfile.value) {
        Object.entries(selectedFilterProfile.value.configs).forEach(([key, values]) => {
            if (props['filters'][key]) {
                Object.entries(values).forEach(([index, val]) => {
                    props['filters'][key][index] = val;
                })
                checkEmptyForm.value = false;
            }
        })
        applyFilters();
    }
}
const mapValue = (template) => {
    formData["geo_radius"]["latitude"] = template.center_lat;
    formData["geo_radius"]["longitude"] = template.center_lon;
    formData["geo_radius"]["data"] = template.radius;
}
const resetFilters = () => {
    selectedFilterProfile.value = null;
    emits('resetFilters');
    emits('update:modelValue', false);
}
const validateEmptyForm = (newFormData) => {
    // Runs from the deep watcher on every keystroke, so the condition select
    // shows the default as soon as a value is typed.
    fillDefaultActions(newFormData);
    checkEmptyForm.value = true;
    for (const [key, filter] of Object.entries(newFormData)) {
        if (filter?.data !== '' && filter?.data !== null) {
            checkEmptyForm.value = false;
        } else {
            filter.data = null;
        }
    }
}
const applyFilters = () => {
    errors.value = [];
    emits('update:modelValue', false);
}

watch(formData, (newFormData) => validateEmptyForm(newFormData), {deep: true});
onMounted(() => {
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    filterProfiles.value = localStorageProfiles?.profiles ?? null;
    if (filterProfiles.value && localStorageProfiles?.selectedProfile) {
        selectedFilterProfile.value = filterProfiles.value.find((item) => item.id === localStorageProfiles.selectedProfile);
    }
    if (!filterProfiles.value) {
        fetchFilterProfile();
    }
    validateEmptyForm(formData)
})
onUnmounted(() => {
    if (filterProfiles.value) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            profiles: filterProfiles.value,
            selectedProfile: selectedFilterProfile.value?.id ?? null
        }));
    }
    // Every fetch is triggered from this emit (Apply, close, profile apply).
    // A profile applied synchronously can unmount the dialog before the deep
    // watcher has run, and a profile saved before conditions existed carries
    // none, so fill here too or that request is the old 422.
    fillDefaultActions(formData);
    emits('applyFilters');
})
</script>

<style scoped>
.filter-dialog {
    overflow: hidden;
}
.filter-header {
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.04), rgba(var(--v-theme-primary), 0.01));
}
.filter-icon-circle {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary, var(--v-theme-primary))));
    display: flex;
    align-items: center;
    justify-content: center;
}
.filter-card {
    border-color: rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
}
.filter-card:hover {
    border-color: rgba(var(--v-theme-primary), 0.3);
    box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.08);
}
.filter-card-active {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.02);
}
</style>
